import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useStore } from '../../contexts/StoreProvider';
import { ArrowLeft, RefreshCw, Package, Truck, MapPin } from 'lucide-react';

const OrderDetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { orders, fetchOrders, isOrdersLoading } = useStore();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const o = orders.find(ord => ord.id === id);
    if (o) {
      setOrder(o);
    } else {
      // If we land here directly, fetch orders
      fetchOrders();
    }
  }, [id, orders, fetchOrders]);

  if (isOrdersLoading && !order) {
    return (
      <div className="flex justify-center py-40">
        <div className="w-10 h-10 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-40 text-gray-500">
        Order not found.
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <button 
          onClick={() => navigate('/store/orders')}
          className="flex items-center text-sm font-semibold tracking-wider uppercase hover:text-gray-600 transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Orders
        </button>
        <button 
          onClick={fetchOrders}
          className="flex items-center text-sm font-semibold tracking-wider uppercase text-gray-500 hover:text-black transition-colors"
        >
          <RefreshCw size={16} className={`mr-2 ${isOrdersLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-white border border-gray-200 p-6 sm:p-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b pb-6 mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-widest uppercase mb-2">Order #{order.order_number}</h1>
            <p className="text-gray-500 text-sm">
              Placed on {new Date(order.created_at).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <span className="bg-gray-100 text-black px-4 py-2 text-xs font-bold uppercase tracking-widest">
              {order.status}
            </span>
            <span className={`px-4 py-2 text-xs font-bold uppercase tracking-widest ${
              order.payment_status?.toLowerCase() === 'paid' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
            }`}>
              {order.payment_status}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-sm font-bold tracking-widest uppercase text-gray-400 mb-4 flex items-center">
              <Truck size={16} className="mr-2" />
              Fulfillment
            </h3>
            <p className="font-semibold">{order.fulfillment_method}</p>
          </div>
          {order.fulfillment_method === 'DELIVERY' && order.shipping_address && (
            <div>
              <h3 className="text-sm font-bold tracking-widest uppercase text-gray-400 mb-4 flex items-center">
                <MapPin size={16} className="mr-2" />
                Shipping Address
              </h3>
              <p className="text-gray-700 whitespace-pre-line">{order.shipping_address}</p>
            </div>
          )}
        </div>

        <h3 className="text-sm font-bold tracking-widest uppercase text-gray-400 mb-4 flex items-center border-t pt-8">
          <Package size={16} className="mr-2" />
          Items
        </h3>
        
        <div className="space-y-4 mb-8">
          {order.items?.map(item => {
            const product = item.product_detail || item.product;
            return (
              <div key={item.id} className="flex items-center gap-4 py-4 border-b border-gray-100">
                <div className="w-16 h-20 bg-gray-100 flex-shrink-0">
                  {product?.images && product.images.length > 0 ? (
                    <img src={product.images[0].image} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-200"></div>
                  )}
                </div>
                <div className="flex-1">
                  <Link to={`/store/product/${product?.id}`} className="font-semibold hover:underline">
                    {product?.name || 'Unknown Product'}
                  </Link>
                  <div className="text-sm text-gray-500 mt-1">Qty: {item.quantity}</div>
                </div>
                <div className="font-bold">
                  ₵{(parseFloat(item.price) * item.quantity).toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>

        {(() => {
          const trackingUpdates = order.tracking_updates && order.tracking_updates.length > 0 
            ? order.tracking_updates 
            : [{
                status: order.status,
                description: `Your order has been received and is currently ${order.status.toLowerCase()}.`,
                created_at: order.created_at
              }];

          return (
            <div className="mb-8">
              <h3 className="text-sm font-bold tracking-widest uppercase text-gray-400 mb-6 border-t pt-8 flex items-center">
                <MapPin size={16} className="mr-2" />
                Tracking History
              </h3>
              <div className="space-y-6">
                {trackingUpdates.map((update, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 bg-black rounded-full mt-1.5"></div>
                      {idx !== trackingUpdates.length - 1 && <div className="w-0.5 h-full min-h-[2rem] bg-gray-200 my-1"></div>}
                    </div>
                    <div className="pb-2">
                      <div className="font-bold uppercase tracking-wider text-sm">{update.status}</div>
                      <div className="text-sm text-gray-600 mt-1">{update.description}</div>
                      <div className="text-xs text-gray-400 mt-2">
                        {new Date(update.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                        {update.location_address ? ` • ${update.location_address}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        <div className="flex justify-end pt-4">
          <div className="w-full sm:w-1/2">
            <div className="flex justify-between items-center text-lg font-bold border-t-2 border-black pt-4">
              <span className="uppercase tracking-wider">Total</span>
              <span>₵{parseFloat(order.total_amount).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailScreen;
